import { useState } from 'react';
import { ApiError, type ContentPageSummary } from '@chicare/shared';

import { useAdminPage, useAdminPages, useSavePage } from '@/lib/adminQueries';
import { cn } from '@/lib/cn';
import { Icon } from '@/components/icons/Icon';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Input } from '@/components/ui/Input';
import { MarkdownEditor } from '@/components/ui/MarkdownEditor';
import { Skeleton } from '@/components/ui/Skeleton';
import { AdminError } from './AdminError';

/**
 * The CMS.
 *
 * Master–detail, because an operator editing four pages in a row should not
 * lose their place in the list between saves.
 *
 * Some pages are load-bearing: §4.5 requires the returns policy readable at
 * checkout and §11 fixes parts of its wording. Those show a lock, cannot be
 * deleted, and say so rather than silently failing when someone tries.
 */
export function ContentScreen() {
  const { data: pages, isLoading, isError, error, refetch } = useAdminPages();
  const [selected, setSelected] = useState<string | null>(null);

  const activeSlug = selected ?? pages?.[0]?.slug;

  if (isError) {
    return <AdminError title="We couldn't load the pages" error={error} onRetry={() => refetch()} />;
  }

  return (
    <div className="mx-auto w-full max-w-[1200px] px-4 py-6 xl:px-8">
      <header className="mb-6">
        <h1 className="m-0 font-display text-[26px] font-bold tracking-[-0.02em]">Pages</h1>
        <p className="mt-1 max-w-[70ch] text-[13px] text-muted">
          Terms, privacy, the returns policy and anything else buyers read. Every save keeps the
          version it replaced — a legal page is a promise, and promises get disputed.
        </p>
      </header>

      <div className="grid gap-4 md:grid-cols-[280px_1fr] md:items-start">
        <Card flush className="md:sticky md:top-4">
          {isLoading ? (
            <div className="space-y-1.5 p-3">
              {Array.from({ length: 4 }).map((_, index) => (
                <Skeleton key={index} className="h-12 w-full" radius="md" />
              ))}
            </div>
          ) : (
            <ul className="m-0 list-none divide-y divide-line-2 p-0">
              {pages?.map((page) => (
                <li key={page.id}>
                  <button
                    type="button"
                    onClick={() => setSelected(page.slug)}
                    className={cn(
                      'w-full px-3.5 py-3 text-left',
                      page.slug === activeSlug ? 'bg-blue-tint-2 shadow-[inset_3px_0_0_var(--color-blue)]' : 'hover:bg-paper',
                    )}
                  >
                    <div className="flex items-center gap-1.5">
                      <b className="truncate text-[13px]">{page.title}</b>
                      {page.is_system ? (
                        <Icon
                          name="shield"
                          size={12}
                          filled
                          className="shrink-0 text-muted"
                          title="System page — editable, not deletable"
                        />
                      ) : null}
                    </div>
                    <div className="mt-1 flex items-center gap-2">
                      <Badge tone={page.status === 'published' ? 'verified' : 'caution'}>
                        {page.status}
                      </Badge>
                      <span className="font-mono text-[10px] text-faint">/{page.slug}</span>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Card>

        {activeSlug ? <PageEditor key={activeSlug} slug={activeSlug} pages={pages ?? []} /> : null}
      </div>
    </div>
  );
}

function PageEditor({ slug, pages }: { slug: string; pages: ContentPageSummary[] }) {
  const { data: page, isLoading } = useAdminPage(slug);
  const save = useSavePage();

  const [draft, setDraft] = useState<Record<string, unknown>>({});
  const [note, setNote] = useState('');

  const summary = pages.find((candidate) => candidate.slug === slug);
  const value = <K extends string>(key: K, fallback: unknown) =>
    (key in draft ? draft[key] : fallback) as string;

  const dirty = Object.keys(draft).length > 0;
  const fieldErrors = save.error instanceof ApiError ? save.error.fieldErrors : {};

  if (isLoading || !page) {
    return (
      <Card>
        <Skeleton className="h-5 w-52" />
        <div className="mt-4 space-y-3">
          <Skeleton className="h-[42px] w-full" radius="md" />
          <Skeleton className="h-64 w-full" radius="md" />
        </div>
      </Card>
    );
  }

  return (
    <div className="min-w-0 space-y-4">
      <Card>
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <h2 className="m-0 font-display text-[17px] font-bold tracking-[-0.015em]">
            {page.title}
          </h2>
          {page.is_system ? (
            <Badge tone="neutral">
              linked from {page.placement === 'checkout' ? 'checkout' : 'the footer'}
            </Badge>
          ) : null}
          <span className="ml-auto text-[11px] text-faint">
            {page.revision_count} revision{page.revision_count === 1 ? '' : 's'}
          </span>
        </div>

        <div className="space-y-4">
          <Input
            label="Title"
            value={value('title', page.title)}
            error={fieldErrors.title?.[0]}
            onChange={(event) => setDraft((d) => ({ ...d, title: event.target.value }))}
          />

          <Input
            label="Short summary"
            value={value('excerpt', page.excerpt ?? '')}
            hint="Shown in link previews and in the page list."
            onChange={(event) => setDraft((d) => ({ ...d, excerpt: event.target.value }))}
          />

          <div>
            <label htmlFor="page-body" className="mb-[5px] block text-[11px] font-bold text-muted">
              Body <span className="font-normal text-faint">· Markdown</span>
            </label>
            {/* The same editor the blog uses. It was a bare textarea, and the
                returns policy is the one page on the platform where a mangled
                list or an unclosed bracket is a promise rendered wrongly — so
                a preview that IS the public renderer matters more here than on
                an article, not less.

                No picture upload: a legal page with a photograph in it is not a
                thing anybody needs, and the button would only be there to be
                pressed by accident. */}
            <MarkdownEditor
              id="page-body"
              value={value('body', page.body ?? '')}
              onChange={(body) => setDraft((d) => ({ ...d, body }))}
              minRows={20}
            />
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <Input
              label="SEO title"
              value={value('seo_title', page.seo_title ?? '')}
              hint="Falls back to the page title."
              onChange={(event) => setDraft((d) => ({ ...d, seo_title: event.target.value }))}
            />
            <div>
              <label htmlFor="page-status" className="mb-[5px] block text-[11px] font-bold text-muted">
                Status
              </label>
              <select
                id="page-status"
                value={value('status', page.status)}
                onChange={(event) => setDraft((d) => ({ ...d, status: event.target.value }))}
                className="min-h-[42px] w-full rounded-inp border border-line bg-white px-3 text-[13px] outline-none focus:border-blue"
              >
                <option value="draft">Draft — not visible to buyers</option>
                <option value="published">Published</option>
              </select>
            </div>
          </div>
        </div>
      </Card>

      {dirty ? (
        <div className="sticky bottom-4 rounded-card border border-blue bg-white p-3.5 shadow-md">
          <Input
            label="What changed, and why?"
            value={note}
            placeholder="e.g. Clarified the 7-day claim window after a support query."
            hint="Kept with the revision, so a future dispute has an answer."
            onChange={(event) => setNote(event.target.value)}
          />
          <div className="mt-3 flex flex-wrap gap-2">
            <Button
              loading={save.isPending}
              onClick={() =>
                save.mutate(
                  {
                    slug,
                    page: {
                      title: value('title', page.title),
                      excerpt: value('excerpt', page.excerpt ?? ''),
                      body: value('body', page.body ?? ''),
                      status: value('status', page.status),
                      seo_title: value('seo_title', page.seo_title ?? ''),
                      change_note: note || undefined,
                    },
                  },
                  { onSuccess: () => { setDraft({}); setNote(''); } },
                )
              }
            >
              Save page
            </Button>
            <Button variant="grey" onClick={() => { setDraft({}); setNote(''); save.reset(); }}>
              Discard
            </Button>
            {summary && !summary.is_deletable ? (
              <span className="ml-auto flex items-center gap-1.5 text-[11px] text-faint">
                <Icon name="shield" size={12} filled />
                System page — editable, never deletable
              </span>
            ) : null}
          </div>
          {save.error instanceof ApiError && !save.error.isValidation ? (
            <p className="mt-2 text-[11px] font-medium text-danger">{save.error.message}</p>
          ) : null}
        </div>
      ) : null}

      {page.revisions.length > 0 ? (
        <Card flush>
          <div className="border-b border-line-2 px-4 py-3">
            <h3 className="m-0 font-display text-[14px] font-bold">History</h3>
          </div>
          <ul className="m-0 list-none divide-y divide-line-2 p-0">
            {page.revisions.slice(0, 8).map((revision) => (
              <li key={revision.version} className="flex flex-wrap items-baseline gap-2 px-4 py-2.5">
                <Badge>v{revision.version}</Badge>
                <span className="text-[12px]">{revision.editor}</span>
                <span className="text-[11px] text-faint">
                  {revision.at ? new Date(revision.at).toLocaleString('en-NG') : ''}
                </span>
                {revision.change_note ? (
                  <span className="w-full text-[11px] italic text-muted">
                    “{revision.change_note}”
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        </Card>
      ) : null}
    </div>
  );
}
